import React from 'react'
import { merge, combineLatest, NEVER, of, from } from 'rxjs'
import { switchMap, tap, filter, map, first } from 'rxjs/operators'
import { forEach, mapValues, range, result, startsWith, pick, flatten, first as ldFirst } from 'libraries/lodash'
import { RESPONSE_NOTFOUND, RESPONSE_PENDING } from 'libraries/front-end-core/constants/responses'
import url from 'libraries/front-end-core/core/url'
import { isValidResponse, flatMapResponse, mapResponse } from 'libraries/front-end-core/helpers/dataflow'
import { observePageState, updatePageState } from 'libraries/front-end-core/services/PageStateService'
import { observeSelected, toggleContent } from 'libraries/front-end-core/services/StateService'
import { getBaseURI } from 'libraries/front-end-core/helpers/document'
import IconButton from '@mui/material/IconButton'
import LinkIcon from '@mui/icons-material/Link'
import { QA_CASE_REF_LINKS, QA_SELECTION } from 'constants/feature-flags'
import { DEFAULT_FLAVOUR } from 'constants/flavours'
import { QA_CONTENT as S_QA_CONTENT } from 'constants/controllers'
import {
  CASE_REFS_CHAPTER,
  QA_CONTENT,
  QA_CONTENT_PREPAY,
  QA_REFERENCES,
  QA_SECTIONS,
  QA_SECTIONS_PREPAY
} from 'constants/requests'
import { URL_WORD_QA } from 'constants/urls'
import { fetch as fetchService, refresh } from 'services/ApiService'
import { accordionContent, accordionGroup } from 'helpers/accordion'
import loggedRequestSwitcher from 'helpers/loggedRequestSwitcher'
import copyToClipboard from 'helpers/copyToClipboard'
import TealToolTip from 'components/TealToolTip'

export const toPath = function (itemPath) {
  return itemPath.join('/') + '/'
}

export const requestSections = function (flavour) {
  return loggedRequestSwitcher(
    () => fetchService(QA_SECTIONS, { flavour }),
    () => fetchService(QA_SECTIONS_PREPAY, { flavour })
  )
}

export const refreshSections = function (flavour) {
  return loggedRequestSwitcher(
    () => refresh(QA_SECTIONS, { flavour }),
    () => refresh(QA_SECTIONS_PREPAY, { flavour })
  )
}

export const requestFlavouredQASections = function (flavours) {
  return combineLatest(
    [...flavours.map(flavour => requestSections(flavour))],
    mapResponse(function () {
      return Object.assign(...flavours.map((flavour, i) => ({ [flavour]: arguments[i] })))
    })
  )
}

export const requestQAReferences = function ({ flavour, references }) {
  return combineLatest([
    fetchService(QA_REFERENCES, { references }),
    requestSections(flavour)],
  mapResponse(({ questions }, { nodes: sections }) => ({
    questions,
    sections
  }))
  )
}

export const toSafeCaseRefMap = (response) => {
  if (response === RESPONSE_PENDING) return response
  return isValidResponse(response) ? response : {}
}

const SectionContentStore = {}

/**
 * Requests list of all sections at root level
 * Requests list of all subsections and questions combined at sublevels
 * @return {Observable}
 */
export const requestSectionContent = function (flavour, sectionId) {
  if (!sectionId || +sectionId < 0) {
    return requestSections(flavour)
  }

  return requestSections(flavour).pipe(
    switchMap(
      flatMapResponse(resp => {
        const rootId = result(resp, ['nodes', sectionId, 'path', 0], null)
        if (rootId === null) {
          return of(RESPONSE_NOTFOUND)
        }
        if (Object.prototype.hasOwnProperty.call(SectionContentStore, flavour + rootId)) {
          return of(SectionContentStore[flavour + rootId])
        } else {
          const sections = result(resp, ['nodes', rootId, 'descendants'], [])
          const paths = {}
          const nodes = pick(resp.nodes, sections)
          forEach(sections, section => {
            paths[section] = resp.nodes[section].path
          })
          const rootSection = [rootId]
          const withoutCaseRefMap = map(mapResponse(content => ({ ...content, paths, nodes, rootId, caseRefMap: {} })))

          return loggedRequestSwitcher(
            () => QA_CASE_REF_LINKS
              ? combineLatest(
                [
                  fetchService(QA_CONTENT, { flavour, sections: rootSection }),
                  fetchService(CASE_REFS_CHAPTER, { chapterId: rootId }).pipe(map(toSafeCaseRefMap))
                ],
                mapResponse((content, caseRefMap) => ({ ...content, paths, nodes, rootId, caseRefMap }))
              )
              : fetchService(QA_CONTENT, { flavour, sections: rootSection }).pipe(withoutCaseRefMap),
            () => fetchService(QA_CONTENT_PREPAY, { flavour, sections: rootSection }).pipe(withoutCaseRefMap)
          )
        }
      })
    ),
    tap(
      mapResponse(resp => {
        SectionContentStore[flavour + resp.rootId] = resp
      })
    )
  )
}

/**
* Expands entire path from root to focusedId
*/
export const expandContentPath = function (flavour, rootId, focusedId) {
  return requestSectionContent(flavour, rootId).pipe(
    filter(isValidResponse),
    map(
      resp =>
        resp.paths[result(resp, ['questions', focusedId, 'sectionId'], focusedId)]
    ),
    tap(path => {
      path &&
        updatePageState(state => {
          const stateUpdates = { [toPath([rootId])]: true }
          for (let i = 0; i < path.length; i++) {
            stateUpdates[toPath([...path.slice(0, i + 1)])] = true
          }
          stateUpdates[toPath([...path.slice(0, 2), 'leaf', path[path.length - 1]])] = true // expand the leaf
          stateUpdates[toPath([...path, 'content'])] = true // expand the content

          return Object.assign(
            {},
            state,
            stateUpdates
          )
        })
    })
  )
}

const listSectionTocIds = function (sectionContent, sectionId) {
  return flatten(
    result(sectionContent, ['nodes', sectionId, 'descendants'], [])
      .map(id => result(sectionContent, ['sections', id], []))
  )
}

const listSectionIds = function (sectionContent, sectionId) {
  return flatten(
    result(sectionContent, ['nodes', sectionId, 'descendants'], [])
  )
}

/**
* QA Question to AccordionContent map
* @return {Object}
*/
const toAccordionContent = function (aol, flavour, path, item, hasAccess = false, caseRefMap = {}) {
  const { tocId, question: title, questionNumber, answer: content } = item
  const [sectionId] = path
  const itemPath = [...path, tocId]
  const expandPath = [...path, 'content']

  const stateSource = merge(
    observePageState(toPath(expandPath)).pipe(map(expanded => ({ expanded: expanded ? hasAccess : false }))),
    QA_SELECTION
      ? observeSelected(flavour, [tocId]).pipe(map(state => ({ selected: state[0].selected })))
      : NEVER
  )

  const id = url(S_QA_CONTENT, { flavour, sectionId, focusedId: tocId, aol })

  return accordionContent({
    id,
    title,
    prefix: `Q${questionNumber}:`,
    path: itemPath,
    content,
    hasAccess,
    stateSource,
    suffix: getContentNodeSuffix(id),
    caseRefMap
  })
}

const getContentNodeSuffix = (id) => (
  <TealToolTip
    arrow
    title='Copy (unique Q&A) link to clipboard'
  >
    <a
      href={null}
      data-clipboard-text={id}
      onClick={() => copyToClipboard(getBaseURI() + id, 'Link copied to clipboard')}
      className='pointer'
    >
      <IconButton size='small'>
        <LinkIcon />
      </IconButton>
    </a>
  </TealToolTip>
)

export const s0stateMap = function (pageState) {
  let expanded = null
  forEach(pageState, (v, k) => {
    const [id, ...tail] = k.split('/')
    if (tail.length <= 1 && v) {
      expanded = id
    }
  })
  return expanded
}

/**
* QA Section to AccordionGroup map
* @return {Object}
*/
const toAccordionGroup = function (aol, flavour, item, sectionContent) {
  const { path, title, id: sectionId, currentUserCanAccessSection } = item
  const [s0, s1] = path
  const id = url(S_QA_CONTENT, { flavour, sectionId: s0, focusedId: sectionId, aol })
  const tocIds = listSectionTocIds(sectionContent, sectionId)
  const sectionIds = listSectionIds(sectionContent, sectionId)
  const hasContent = Object.prototype.hasOwnProperty.call(sectionContent.sections, sectionId)
  const leafPath = toPath([s0, s1, 'leaf', sectionId])
  const hasAccess = currentUserCanAccessSection

  switch (path.length) {
    case 1:
      return accordionGroup({
        id,
        title,
        path,
        tocIds,
        sectionIds,
        hasAccess,
        groupSource: requestGroup({ aol, flavour, parentId: sectionId }),
        stateSource: observePageState().pipe(
          map(s0stateMap), // maps state to id of expanded S0
          map(expandedId =>
            ({
              expanded: expandedId === sectionId,
              display: !expandedId || expandedId === sectionId
            })
          ) // leaves 1 expanded S0 displayed
        )
      })
    case 2:
      return accordionGroup({
        id,
        title,
        path,
        tocIds,
        sectionIds,
        disabled: !item.active,
        hasAccess,
        comingSoon: tocIds.length === 0,
        focusedId: sectionId,
        groupSource: requestGroup({ aol, flavour, parentId: sectionId }),
        stateSource: merge(
          observePageState(toPath(path)).pipe(map(expanded => ({ expanded, suffix: expanded ? 'Click to collapse all' : '' })))
        )
      })
    default:
      return accordionGroup({
        id,
        title,
        tocIds,
        sectionIds,
        path: hasContent ? [s0, s1, 'leaf', sectionId] : path,
        hasAccess,
        groupSource: requestGroup({ aol, flavour, parentId: sectionId }),
        stateSource: merge(
          observePageState().pipe(map(s => ({ expanded: !!(s[leafPath] || s[toPath(path)]) }))),
          QA_SELECTION
            ? observeSelected(flavour, tocIds).pipe(map(r => ({ selected: r.reduce((a, i) => a && i.selected, 1) })))
            : NEVER
        )
      })
  }
}

/**
* Observable to feed Accordion at any level with list of Questions and Sections
* @return {Observable}
*/
export const requestGroup = function ({ aol, flavour, parentId = '-1' }) {
  return requestSectionContent(flavour, parentId)
    .pipe(
      map(mapResponse(
        resp => {
          const parentNode = resp.nodes[parentId]

          return [
            ...result(resp, ['sections', parentId], []).map(tocId => toAccordionContent(aol, flavour, resp.paths[parentId], resp.questions[tocId], parentNode.currentUserCanAccessSection, resp.caseRefMap)), // content
            ...result(resp, ['nodes', parentId, 'childSections'], []).map(id => {
              const childNode = resp.nodes[id]
              // Child inherits parent's access state - if parent doesn't have access, neither do children
              const childWithInheritedAccess = {
                ...childNode,
                currentUserCanAccessSection:
                  parentNode.currentUserCanAccessSection
                    ? childNode.currentUserCanAccessSection
                    : false
              }
              return toAccordionGroup(aol, flavour, childWithInheritedAccess, resp)
            })
          ]
        }
      )),
      map(mapResponse(
        resp => (resp.length && resp) || [accordionContent({ content: ' ' })]
      ))
    )
}

export const expandCallback = function (pathStr, type, flavour, value) {
  const path = pathStr.split('/')
  const [rootId, groupId] = path
  const expandPath = path && [...path.slice(0, -1), 'content']

  switch (type) {
    case 'content':
      updatePageState(state => Object.assign({}, state, { [toPath(expandPath)]: !!value }))
      return true
    case 'group':
      updatePageState(state => {
        if (!value) {
          state = Object.assign({}, state, ...range(path.length).map(p => ({ [toPath(path.slice(0, p))]: true })))
          state = mapValues(state, (v, p) =>
            (startsWith(p, pathStr) || p === toPath([rootId, groupId, 'leaf'])) ||
            p === toPath([rootId, groupId, 'leaf', path[path.length - 1]])
              ? false
              : v) // collapse the leaf of the content
        } else {
          const s1 = toPath(path.slice(0, 2))
          state = mapValues(state, (v, p) => !startsWith(p, s1) ? false : v)
          state = Object.assign({}, state, ...range(path.length).map(p => ({ [toPath(path.slice(0, p + 1))]: true })))
        }
        return state
      })
      return true
  }
}

export const selectCallback = function (path, type, flavour, value) {
  const tocId = path && path.split('/').pop()
  const sectionId = path && path.split('/').pop()
  switch (type) {
    case 'content':
      toggleContent(value, flavour, tocId)
      return true
    case 'group':
      requestSectionContent(flavour, sectionId).pipe(
        filter(isValidResponse),
        first(),
        map(sectionContent => {
          return toggleContent(value, flavour, listSectionTocIds(sectionContent, sectionId))
        })
      ).subscribe(x => { })
      return true
  }
}

export const getTopLevelSections = (sections, rootId, includeComingSoon = false) => {
  return Object.keys(sections).reduce((acc, curr) => {
    const section = sections[curr]
    if (section.parentId === rootId) {
      const isEmpty = section.childSections.length < 1
      const hasAccess = section.currentUserCanAccessSection
      const allS1sEmpty = includeComingSoon && !isEmpty && section.childSections.every(s1Id => {
        const s1 = sections[s1Id]
        return !s1 || s1.childSections.length === 0
      })

      return [
        ...acc,
        {
          ...section,
          empty: isEmpty,
          dull: !isEmpty && !hasAccess,
          comingSoon: allS1sEmpty
        }
      ]
    }

    return acc
  }, [])
}

export const getDescendants = (nodes, sectionId, flavour = DEFAULT_FLAVOUR, aol) => {
  const node = nodes[sectionId]
  if (!node) {
    return []
  }
  const descendants = (node.childSections ?? [])
    .map(childId => getDescendants(nodes, childId, flavour, aol))
    .flat()
  if (!node.childSections || node.childSections.length === 0) {
    const path = node.path.length === 1
      ? url(S_QA_CONTENT, { flavour, sectionId: node.id, aol })
      : url(S_QA_CONTENT, { flavour, sectionId: ldFirst(node.path), focusedId: node.id, aol })
    return [{ title: node.title, sectionId, path }]
  }

  return [...descendants]
}

const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

const fetchWordDocument = async (ids, removeCrossRefs) => {
  const response = await fetch(URL_WORD_QA, {
    method: 'post',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, removeCrossRefs })
  })
  if (!response.ok) throw new Error(`Word download failed: ${response.status}`)
  const disposition = response.headers.get('Content-Disposition')
  const filename = disposition?.split('filename=')[1]?.replace(/['"]/g, '').trim() || 'document.docx'
  const blob = await response.blob()
  triggerDownload(blob, filename)
}

export const requestWordDocument = (ids, removeCrossRefs) => from(fetchWordDocument(ids, removeCrossRefs))

export const getUniquelyTitledDescendants = (nodes, descendants) => {
  const repeatingTitles = descendants.reduce((acc, curr) => {
    if (acc.some(title => title === curr.title)) {
      return acc
    }

    const match = descendants.some(descendant => {
      return descendant.title === curr.title && descendant.path !== curr.path
    })

    if (match) {
      return [...acc, curr.title]
    }

    return acc
  }, [])

  return descendants.map(descendant => {
    if (repeatingTitles.some(title => title === descendant.title)) {
      const node = nodes[descendant.sectionId]
      const parentSectionId = node.path[node.path.length - 2]
      const parentNode = nodes[parentSectionId]

      return {
        ...descendant,
        title: `${descendant.title} (${parentNode.title})`
      }
    }

    return descendant
  })
}
